import type { CropKf, Word } from "./clip.js";

export interface FaceSample { ms: number; cx: number | null; cy?: number; w?: number; h?: number; score?: number }
export interface FacePoint { ms: number; cx: number; cy: number; w: number }

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
}

/** Drops empty samples, median-filters (window 5) then exponentially smooths the face centre. */
export function smoothFaces(samples: FaceSample[], alpha = 0.35): FacePoint[] {
  const pts = samples.filter((s): s is FaceSample & { cx: number; cy: number; w: number } => s.cx != null && s.cy != null && s.w != null);
  if (!pts.length) return [];
  const med = pts.map((p, i) => {
    const win = pts.slice(Math.max(0, i - 2), i + 3);
    return { ms: p.ms, cx: median(win.map((x) => x.cx)), cy: median(win.map((x) => x.cy)), w: median(win.map((x) => x.w)) };
  });
  const out: FacePoint[] = [];
  for (const p of med) {
    const prev = out[out.length - 1];
    out.push(prev ? { ms: p.ms, cx: prev.cx + alpha * (p.cx - prev.cx), cy: prev.cy + alpha * (p.cy - prev.cy), w: prev.w + alpha * (p.w - prev.w) } : p);
  }
  return out;
}

export function faceAt(points: FacePoint[], ms: number): { cx: number; cy: number } {
  if (!points.length) return { cx: 0.5, cy: 0.4 };
  if (ms <= points[0].ms) return points[0];
  const i = points.findIndex((p) => p.ms >= ms);
  if (i < 0) return points[points.length - 1];
  const a = points[i - 1], b = points[i];
  const t = (ms - a.ms) / Math.max(1, b.ms - a.ms);
  return { cx: a.cx + (b.cx - a.cx) * t, cy: a.cy + (b.cy - a.cy) * t };
}

export const norm = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}']/gu, "");

/** Function words that never deserve emphasis on their own. */
export const STOP_WORDS = new Set(["the", "a", "an", "of", "to", "and", "or", "for", "in", "on", "at", "by", "with", "from", "is", "are", "was", "were", "be", "that", "this", "his", "her", "their", "our", "your", "my", "its", "as", "but", "so", "if", "than", "into", "about", "not", "no", "it", "he", "she", "they", "we", "you", "i", "him", "them", "us", "me", "do", "does", "did", "have", "has", "had", "will", "would", "can", "could", "there", "here", "then", "when", "what", "who", "how", "why", "which", "all", "any", "some", "one", "up", "out", "just", "very", "also"]);

/** Word occurrences that match emphasis words/phrases (single words or exact multi-word phrases). Lone function words are ignored. */
export function findEmphasis(words: Word[], emphasis: string[]): { startMs: number; endMs: number; idx: number[] }[] {
  const hits: { startMs: number; endMs: number; idx: number[] }[] = [];
  const phrases = emphasis.map((e) => e.split(/\s+/).map(norm).filter(Boolean)).filter((p) => p.length && !(p.length === 1 && STOP_WORDS.has(p[0])));
  for (let i = 0; i < words.length; i++) {
    for (const p of phrases) {
      if (p.every((tok, k) => words[i + k] && norm(words[i + k].text) === tok)) {
        hits.push({ startMs: words[i].startMs, endMs: words[i + p.length - 1].endMs, idx: Array.from({ length: p.length }, (_, k) => i + k) });
      }
    }
  }
  return hits.sort((a, b) => a.startMs - b.startMs);
}

export interface CropOptions {
  zoom?: number;
  leadMs?: number;
  holdMs?: number;
  releaseMs?: number;
  minGapMs?: number;
  offset?: { dx: number; dy: number };
  follow?: "auto" | "static" | "track";
  maxZooms?: number;
}

const quantile = (xs: number[], q: number) => { const s = [...xs].sort((a, b) => a - b); return s[Math.min(s.length - 1, Math.floor(q * s.length))]; };

/** The path the crop window follows: one fixed point for a seated speaker, or a dead-zone-eased track when they really move. */
export function cropPath(points: FacePoint[], follow: "auto" | "static" | "track" = "auto"): FacePoint[] {
  if (!points.length) return [{ ms: 0, cx: 0.5, cy: 0.4, w: 0.1 }];
  const spreadX = quantile(points.map((p) => p.cx), 0.9) - quantile(points.map((p) => p.cx), 0.1);
  const spreadY = quantile(points.map((p) => p.cy), 0.9) - quantile(points.map((p) => p.cy), 0.1);
  const isStatic = follow === "static" || (follow === "auto" && spreadX < 0.05 && spreadY < 0.07);
  if (isStatic) return [{ ms: points[0].ms, cx: quantile(points.map((p) => p.cx), 0.5), cy: quantile(points.map((p) => p.cy), 0.5), w: quantile(points.map((p) => p.w), 0.5) }];
  const out: FacePoint[] = [{ ...points[0] }];
  let cur = { ...points[0] };
  for (const p of points.slice(1)) {
    const dx = p.cx - cur.cx, dy = p.cy - cur.cy;
    if (Math.abs(dx) > 0.03 || Math.abs(dy) > 0.04) cur = { ms: p.ms, cx: cur.cx + dx * 0.2, cy: cur.cy + dy * 0.2, w: p.w };
    else cur = { ...cur, ms: p.ms };
    out.push({ ...cur });
  }
  return out;
}

/** Crop keyframes (source ms): a calm crop path plus a few punch zooms on emphasised words, each locked in place so nothing drifts while zoomed. */
export function buildCropKeyframes(points: FacePoint[], emphasisRuns: { startMs: number; endMs: number; manual?: boolean }[], opts: CropOptions = {}): CropKf[] {
  const zoom = opts.zoom ?? 1.08, lead = opts.leadMs ?? 120, hold = opts.holdMs ?? 0, release = opts.releaseMs ?? 350, minGap = opts.minGapMs ?? 6000;
  const maxZooms = opts.maxZooms ?? 3;
  const dx = opts.offset?.dx ?? 0, dy = opts.offset?.dy ?? 0;
  const clampX = (x: number) => Math.min(0.83, Math.max(0.17, x + dx));
  const clampY = (y: number) => Math.min(0.9, Math.max(0.1, y + dy));
  const path = cropPath(points, opts.follow ?? "auto");
  const kfs: CropKf[] = path.map((p) => ({ ms: p.ms, cx: clampX(p.cx), cy: clampY(p.cy), zoom: 1 }));
  if (zoom <= 1.001 || maxZooms <= 0) return kfs;
  let lastZoom = -Infinity, used = 0;
  for (const h of [...emphasisRuns].sort((a, b) => a.startMs - b.startMs)) {
    // manual marks are always honoured; automatic ones are rationed and spaced out
    if (!h.manual && (used >= maxZooms || h.startMs - lastZoom < minGap)) continue;
    if (h.startMs - lastZoom < 700) continue; // never two zooms on top of each other
    lastZoom = h.startMs; if (!h.manual) used++;
    const f = faceAt(path, h.startMs);
    const at = (ms: number, z: number) => kfs.push({ ms, cx: clampX(f.cx), cy: clampY(f.cy), zoom: z });
    at(h.startMs - lead, 1);
    at(h.startMs + 80, zoom);
    at(h.endMs + hold, zoom);
    at(h.endMs + hold + release, 1);
  }
  return kfs.sort((a, b) => a.ms - b.ms);
}

/** Groups consecutive emphasised words into runs (source ms). */
export function emphasisRuns(words: Word[]): { startMs: number; endMs: number }[] {
  const runs: { startMs: number; endMs: number }[] = [];
  for (let i = 0; i < words.length; i++) {
    if (!words[i].emphasis) continue;
    const last = runs[runs.length - 1];
    if (last && words[i - 1]?.emphasis) last.endMs = words[i].endMs;
    else runs.push({ startMs: words[i].startMs, endMs: words[i].endMs });
  }
  return runs;
}
